import { Box, ButtonBase, styled, SxProps, Theme } from '@mui/material';
import { ROW_CONTENT_PAD_Y } from 'src/components/Assistant/policies-tab/policy-card.styles';
import {
  getSeverityColors,
  PolicySeverity,
} from 'src/components/Assistant/policies-tab/policy-severity';

export const PreviewCard = styled(Box, { name: 'ActivePoliciesPreview' })(({ theme }) => ({
  position: 'relative',
  flexShrink: 0,
  margin: '0.25rem 0.375rem 0',
  padding: '0.5rem 0.625rem 0.5625rem',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
  borderRadius: '0.625rem',
}));

export const PREVIEW_COLLAPSE_MS = 240;

export const PreviewHeader = styled(Box, { name: 'ActivePoliciesPreviewHeader' })({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.375rem',
});

// the label-to-list gap lives inside the collapse, so it folds away with the list
export const PreviewBody = styled(Box, { name: 'ActivePoliciesPreviewBody' })({
  paddingTop: '0.3125rem',
});

export const PreviewLabel = styled('span', { name: 'ActivePoliciesPreviewLabel' })(
  ({ theme }) => ({
    display: 'block',
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.07em',
    lineHeight: 'normal',
    textTransform: 'uppercase',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  }),
);

// negative block margin keeps the header as tall as the bare label it sits beside
// the caret points up while open, like the category headers below it
export const MinimizeButton = styled(ButtonBase, {
  name: 'ActivePoliciesPreviewMinimize',
  shouldForwardProp: (prop) => prop !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
  display: 'grid',
  placeItems: 'center',
  width: '1.25rem',
  height: '1.25rem',
  margin: '-0.25rem -0.25rem -0.25rem 0',
  borderRadius: '0.3125rem',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  transition: 'background-color .12s, color .12s',

  '& svg': {
    transform: open ? 'rotate(180deg)' : 'none',
    transition: `transform ${PREVIEW_COLLAPSE_MS}ms ease`,
  },
  '@media (prefers-reduced-motion: reduce)': {
    '& svg': { transition: 'none' },
  },

  '&:hover': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: theme.palette.custom.OV.sys.onBackground,
  },
  '&:focus-visible': {
    outline: `0.125rem solid ${theme.palette.custom.OV.sys.primary}`,
    outlineOffset: '0.0625rem',
  },
}));

const EDGE_FADE = '1.375rem';

// edge fades stand in for a counter: the list just looks like it continues
export const getStageSx = (moreAbove: boolean, moreBelow: boolean): SxProps<Theme> => {
  const mask = (() => {
    if (moreAbove && moreBelow) {
      return `linear-gradient(to bottom, transparent, #000 ${EDGE_FADE}, #000 calc(100% - ${EDGE_FADE}), transparent)`;
    }
    if (moreBelow) {
      return `linear-gradient(to bottom, #000 calc(100% - ${EDGE_FADE}), transparent)`;
    }
    if (moreAbove) {
      return `linear-gradient(to top, #000 calc(100% - ${EDGE_FADE}), transparent)`;
    }
    return undefined;
  })();

  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '15.625rem',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': { width: '0.375rem' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
      borderRadius: '0.1875rem',
    },
    ...(mask && { maskImage: mask, WebkitMaskImage: mask }),
  };
};

export const PolicyRow = styled(ButtonBase, {
  name: 'ActivePoliciesPreviewRow',
  shouldForwardProp: (prop) => prop !== 'severity',
})<{ severity: PolicySeverity }>(({ theme, severity }) => {
  const colors = getSeverityColors(theme, severity);

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    // ButtonBase centres its children; this row lays them out from the start edge
    justifyContent: 'flex-start',
    gap: '0.4375rem',
    width: '100%',
    padding: '0.3125rem 0.375rem 0.3125rem 0.5625rem',
    border: 0,
    borderRadius: '0.375rem',
    backgroundColor: colors.fill,
    textAlign: 'left',
    font: 'inherit',
    cursor: 'pointer',
    transition: 'background-color .12s',

    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '0.375rem',
      bottom: '0.375rem',
      width: '0.15625rem',
      borderRadius: '0.125rem',
      backgroundColor: colors.rail,
    },

    '&:hover': { backgroundColor: colors.fillActive },
    '&:focus-visible': {
      outline: `0.125rem solid ${theme.palette.custom.OV.sys.primary}`,
      outlineOffset: '0.0625rem',
    },
  };
});

export const RowBody = styled(Box, { name: 'ActivePoliciesPreviewBody' })({
  minWidth: 0,
  flex: 1,
  paddingTop: ROW_CONTENT_PAD_Y,
  paddingBottom: ROW_CONTENT_PAD_Y,
});

export const RowName = styled(Box, { name: 'ActivePoliciesPreviewName' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.375rem',
  minWidth: 0,
  fontSize: '0.78125rem',
  fontWeight: 600,
  lineHeight: 1.35,
  color: theme.palette.custom.OV.sys.onBackground,
}));

// wrap within the drawer width so long policy titles stay readable
export const RowTitle = styled('span', { name: 'ActivePoliciesPreviewTitle' })({
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
});

// a multi-line summary (paragraphs, list steps) gets a third line so its first step shows
export const RowDesc = styled(Box, {
  name: 'ActivePoliciesPreviewDesc',
  shouldForwardProp: (prop) => prop !== 'multiline',
})<{ multiline: boolean }>(({ theme, multiline }) => ({
  marginTop: '0.1875rem',
  fontSize: '0.6875rem',
  lineHeight: 1.4,
  letterSpacing: 'normal',
  color: theme.palette.custom.OV.sys.onSurface,
  whiteSpace: 'pre-line',
  display: '-webkit-box',
  WebkitLineClamp: multiline ? 3 : 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

export const ScrollCue = styled('button', { name: 'ActivePoliciesPreviewScrollCue' })(
  ({ theme }) => ({
    position: 'absolute',
    left: '50%',
    bottom: '0.4375rem',
    transform: 'translateX(-50%)',
    display: 'grid',
    placeItems: 'center',
    width: '1.5rem',
    height: '1.5rem',
    padding: 0,
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    borderRadius: '999px',
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: theme.palette.custom.OV.sys.onBackground,
    cursor: 'pointer',
    transition: 'opacity .18s',
    boxShadow: theme.shadows[2],
    zIndex: 4,

    '&:hover': { backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest },
  }),
);
